import { resolveDate } from './formatters';

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  pending: 'Pending',
  served: 'Attended',
  attended: 'Attended',
  missed: 'Did not attend',
  'no-show': 'Did not attend',
  absent: 'Absent from school',
  late: 'Late',
  partial: 'Partial attendance',
  excused: 'Excused',
  voided: 'Voided',
};

const ACTION_LABELS = {
  served: 'Marked attended',
  missed: 'Marked did not attend',
  absent: 'Marked absent from school',
  late: 'Marked late',
  reset: 'Cleared attendance outcome',
  excused: 'Excused obligation',
  voided: 'Voided incorrect obligation',
  'resolve-follow-up': 'Follow-up decision recorded',
  rescheduled: 'Rescheduled obligation',
  'undo-reschedule': 'Restored previous session',
  'complete-follow-up': 'Follow-up completed',
  'record-follow-up': 'Follow-up contact recorded',
  'schedule-next-availability': 'Returned for another session',
  'auto-reschedule-limit': 'Automatic rescheduling needs review',
};

const FOLLOW_UP_OUTCOME_LABELS = {
  'attended-correction': 'Changed attendance to attended',
  'attended-late': 'Changed attendance to late',
  'absent-from-school': 'Absent from school — awaiting another session',
  'did-not-complete': 'Did not complete — awaiting another session',
  'learner-contacted': 'Learner contacted',
  'guardian-contacted': 'Guardian contacted',
  'resolved-other': 'Follow-up completed',
  'scheduled-next-availability': 'Returned for another session',
};

const ATTENDANCE_ACTIONS = {
  attended: ['served'],
  served: ['served'],
  'no-show': ['missed'],
  missed: ['missed'],
  absent: ['absent'],
  late: ['late'],
};

const value = (event, key) => event?.[key] ?? event?.originalData?.[key] ?? null;

const humanize = input => String(input || '')
  .replace(/[-_]+/g, ' ')
  .replace(/^./, character => character.toUpperCase());

const isOpaqueActorName = (name, actorId) => {
  const candidate = String(name || '').trim();
  if (!candidate) return true;
  if (actorId && candidate === String(actorId).trim()) return true;
  return /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[A-Za-z0-9_-]{24,36}$/.test(candidate);
};

const resolvedActorName = (name, actorId, actorNamesById = {}) => {
  if (!isOpaqueActorName(name, actorId)) return name;
  if (actorId && actorNamesById[actorId]) return actorNamesById[actorId];
  return actorId ? 'Staff member (name not recorded)' : 'Staff member not recorded';
};

export function obligationActionLog(event = {}) {
  const log = value(event, 'actionLog');
  return Array.isArray(log) ? [...log].sort((left, right) => {
    const leftTime = resolveDate(left?.timestamp)?.getTime() || 0;
    const rightTime = resolveDate(right?.timestamp)?.getTime() || 0;
    return rightTime - leftTime;
  }) : [];
}

export function auditActorName(event, prefix, log = obligationActionLog(event), actorNamesById = {}) {
  const explicitName = value(event, `${prefix}ByName`);
  const actorId = value(event, `${prefix}By`);
  const matchingEntry = log.find(entry => entry?.userId === actorId && entry?.userName);
  return resolvedActorName(explicitName || matchingEntry?.userName, actorId, actorNamesById);
}

export function obligationCurrentAudit(event = {}, actorNamesById = {}) {
  const log = obligationActionLog(event);
  const rows = [];
  const creationEntry = log.find(entry =>
    /(?:^|[-_])created$/.test(String(entry?.action || ''))
  );
  const createdAt = value(event, 'issuedAt') || value(event, 'createdAt') ||
    value(event, 'timestamp') || creationEntry?.timestamp;
  const creatorName = value(event, 'issuedByName') || value(event, 'createdByName') ||
    value(event, 'assignedByName') || creationEntry?.userName;
  const creatorId = value(event, 'issuedById') || value(event, 'createdBy') ||
    value(event, 'createdById') || value(event, 'assignedBy') || creationEntry?.userId;

  if (createdAt) {
    rows.push({
      id: 'created',
      label: 'Obligation created',
      actor: String(creatorId || '').startsWith('system-')
        ? 'Swan'
        : resolvedActorName(creatorName, creatorId, actorNamesById),
      timestamp: createdAt,
    });
  }

  const attendanceStatus = value(event, 'attendanceStatus');
  const status = value(event, 'status');
  const expectedActions = ATTENDANCE_ACTIONS[attendanceStatus] || [];
  const attendanceEntry = log.find(entry =>
    expectedActions.includes(entry?.action) ||
    entry?.newStatus === status
  ) || (creationEntry &&
    ['served', 'missed', 'absent', 'late'].includes(status)
    ? creationEntry
    : null);
  const attendanceAt = value(event, 'attendanceMarkedAt') || attendanceEntry?.timestamp;

  if (attendanceStatus && attendanceAt) {
    rows.push({
      id: 'attendance',
      label: `Attendance recorded: ${STATUS_LABELS[attendanceStatus] || attendanceStatus}`,
      actor: value(event, 'attendanceMarkedBy') || value(event, 'attendanceMarkedByName')
        ? auditActorName(event, 'attendanceMarked', log, actorNamesById)
        : resolvedActorName(
          attendanceEntry?.userName,
          attendanceEntry?.userId,
          actorNamesById
        ),
      timestamp: attendanceAt,
    });
  }

  const decisions = [
    ['excused', 'Excused'],
    ['voided', 'Voided as incorrect'],
  ];
  decisions.forEach(([prefix, label]) => {
    const timestamp = value(event, `${prefix}At`);
    if (!timestamp) return;
    rows.push({
      id: prefix,
      label,
      actor: auditActorName(event, prefix, log, actorNamesById),
      timestamp,
      reason: value(event, `${prefix}Reason`) || value(event, 'voidReason'),
    });
  });

  return rows;
}

export function obligationHistory(event = {}, actorNamesById = {}) {
  const entries = obligationActionLog(event).filter(entry => {
    if (ACTION_LABELS[entry?.action]) return true;
    if (entry?.metadata?.outcome) return true;
    return Boolean(
      entry?.previousStatus &&
      entry?.newStatus &&
      entry.previousStatus !== entry.newStatus
    );
  });
  const returnEntries = [...entries].reverse().filter(entry =>
    entry?.newStatus === 'pending' && (
      entry?.action === 'resolve-follow-up' ||
      entry?.action === 'schedule-next-availability'
    )
  );

  return entries.map((entry, index) => {
    const outcome = entry?.metadata?.outcome;
    const label = FOLLOW_UP_OUTCOME_LABELS[outcome] ||
      ACTION_LABELS[entry?.action] ||
      humanize(entry?.action || 'Updated obligation');

    const previous = STATUS_LABELS[entry?.previousStatus] || entry?.previousStatus;
    const next = STATUS_LABELS[entry?.newStatus] || entry?.newStatus;
    const returnIndex = returnEntries.indexOf(entry);
    const isRepeatedReturn = returnIndex >= 0 && returnEntries.length > 1;
    const transition = isRepeatedReturn
      ? `Follow-up decision ${returnIndex + 1} of ${returnEntries.length}`
      : entry?.newStatus === 'pending'
        ? null
        : previous && next && previous !== next
          ? `${previous} → ${next}`
          : null;
    return {
      id: `${entry?.timestamp?.seconds || entry?.timestamp || 'log'}-${index}`,
      label,
      actor: resolvedActorName(entry?.userName, entry?.userId, actorNamesById),
      timestamp: entry?.timestamp,
      transition,
      details: entry?.details || null,
    };
  });
}

export function obligationAdministrationTimeline(event = {}, actorNamesById = {}) {
  const current = obligationCurrentAudit(event, actorNamesById);
  const history = obligationHistory(event, actorNamesById);
  const historyTimes = new Set(
    history
      .map(row => resolveDate(row.timestamp)?.getTime())
      .filter(time => time != null)
  );

  // The current-state attendance/decision row and its action-log entry usually
  // describe the same write. Keep the richer historical row once, while still
  // retaining creation provenance and current facts that have no audit entry.
  const unduplicatedCurrent = current.filter(row => {
    if (row.id === 'created') return true;
    const time = resolveDate(row.timestamp)?.getTime();
    return time == null || !historyTimes.has(time);
  });

  return [...unduplicatedCurrent, ...history].sort((left, right) => {
    const leftTime = resolveDate(left.timestamp)?.getTime();
    const rightTime = resolveDate(right.timestamp)?.getTime();
    if (leftTime == null) return 1;
    if (rightTime == null) return -1;
    return leftTime - rightTime;
  });
}
